<template>
  <div class="page-container">
    <div class="toolbar">
      <span class="store-tag">粮库：<b>{{ storeName || ('#' + grainStoreId) }}</b></span>
      <el-upload
        v-if="!planImage"
        :show-file-list="false"
        :before-upload="beforeUpload"
        :http-request="handlePlanUpload"
        accept="image/*">
        <el-button type="primary" size="small">上传平面图</el-button>
      </el-upload>
      <el-button v-else size="small" text type="danger" @click="planImage=''">移除平面图</el-button>

      <span class="zoom-box" v-if="planImage">
        <el-button-group>
          <el-button size="small" @click="zoomOut" :disabled="zoom<=0.3">－</el-button>
          <el-button size="small" @click="zoomReset" style="width:64px">{{ Math.round(zoom*100) }}%</el-button>
          <el-button size="small" @click="zoomIn" :disabled="zoom>=3">＋</el-button>
        </el-button-group>
      </span>

      <span class="hint" v-if="planImage">
        <i>双击</i> 空白处添加摄像机 · 拖拽空白处平移平面图 · 滚轮缩放 · 拖拽图标移动摄像机 · 点击图标编辑枪机朝向
      </span>
    </div>

    <div v-if="planImage" class="plan-wrap">
      <div class="plan-scroll" ref="scrollRef" @wheel.prevent="onWheel">
        <!-- 画布：缩放(zoom)与平移(pan)统一通过 transform 实现，标记是画布子元素会一起移动 -->
        <div class="plan-canvas" ref="canvasRef"
             :style="canvasStyle"
             @mousedown="startPan"
             @dblclick="onPlanDblClick">
          <img :src="planImage" class="plan-img" @load="onImgLoad" draggable="false" />
          <div
            v-for="m in monitors"
            :key="m.id"
            class="marker"
            :class="[m.cameraType === 'bullet' ? 'bullet' : 'dome', m.status === 1 ? 'online' : 'offline', draggingId === m.id ? 'dragging' : '']"
            :style="{ left: (m.posX || 0) + '%', top: (m.posY || 0) + '%' }"
            @mousedown.stop.prevent="startDrag($event, m)"
            @dblclick.stop>
            <CameraIcon
              :type="m.cameraType === 'bullet' ? 'bullet' : 'dome'"
              :status="m.status"
              :direction="m.angle || 0"
              show-fov
              size="40"
            />
          </div>
          <!-- 新增时尚未保存的临时标记 -->
          <div v-if="pending" class="marker pending" :class="formData.cameraType === 'bullet' ? 'bullet' : 'dome'"
               :style="{ left: pending.x + '%', top: pending.y + '%' }">
            <CameraIcon
              :type="formData.cameraType === 'bullet' ? 'bullet' : 'dome'"
              :pending="true"
              :direction="formData.angle || 0"
              show-fov
              size="40"
            />
          </div>
        </div>
      </div>

      <!-- 右侧：已标注摄像机管理列表 -->
      <div class="marker-list">
        <div class="marker-list-title">已标注摄像机（{{ monitors.length }}）</div>
        <div class="marker-list-body">
          <el-empty v-if="!monitors.length" description="双击平面图添加摄像机" :image-size="60" />
          <div v-for="m in monitors" :key="'l'+m.id" class="marker-item" @click="editMarker(m)">
            <CameraIcon :type="m.cameraType === 'bullet' ? 'bullet' : 'dome'" :status="m.status" :size="26" />
            <span class="name">{{ m.monitorName }}</span>
            <span class="type-tag">{{ m.cameraType === 'bullet' ? '枪机' : '球机' }}</span>
            <el-button size="small" text type="danger" @click.stop="handleDelete(m.id)">删除</el-button>
          </div>
        </div>
      </div>
    </div>

    <el-empty v-else description="请先上传该粮库的平面图" />

    <!-- 摄像机表单 -->
    <el-dialog
      :title="formData.id ? '编辑摄像机' : '添加摄像机'"
      v-model="dialogVisible"
      width="560px"
      draggable
      :align-center="false"
      class="cam-dialog"
      @close="onDialogClose"
    >
      <el-form :model="formData" label-width="100px">
        <el-form-item label="摄像机名称">
          <el-input v-model="formData.monitorName" placeholder="如：A区南墙球机" />
        </el-form-item>
        <el-form-item label="摄像机类型">
          <el-radio-group v-model="formData.cameraType">
            <el-radio value="dome">球机</el-radio>
            <el-radio value="bullet">枪机</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="朝向角度" v-if="formData.cameraType === 'bullet'">
          <div class="angle-row">
            <el-slider v-model="formData.angle" :min="0" :max="360" :step="1" style="flex:1" />
            <el-input-number v-model="formData.angle" :min="0" :max="360" :step="1" controls-position="right" style="width:90px;margin-left:12px" />
            <div class="angle-preview">
              <CameraIcon type="bullet" :status="formData.status" :direction="formData.angle || 0" show-fov size="48" />
            </div>
          </div>
        </el-form-item>
        <el-form-item label="所属仓房">
          <el-select v-model="formData.warehouseId" clearable placeholder="可选" style="width:100%">
            <el-option v-for="w in warehouses" :key="w.id" :label="w.warehouseName" :value="w.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="IP地址">
          <el-input v-model="formData.ipAddress" />
        </el-form-item>
        <el-form-item label="端口">
          <el-input v-model="formData.port" />
        </el-form-item>
        <el-form-item label="视频流地址">
          <el-input v-model="formData.streamUrl" placeholder="HLS/FLV 等" />
        </el-form-item>
        <el-form-item label="用户名">
          <el-input v-model="formData.username" />
        </el-form-item>
        <el-form-item label="密码">
          <el-input v-model="formData.password" type="password" show-password />
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="formData.status">
            <el-option label="在线" :value="1" />
            <el-option label="离线" :value="0" />
          </el-select>
        </el-form-item>
        <el-form-item label="平面图位置">
          <span>X: {{ formData.posX || 0 }}% &nbsp; Y: {{ formData.posY || 0 }}%</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSave">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import CameraIcon from '../components/CameraIcon.vue'
import {
  getGrainStoreById, getMonitorByGrainStoreId, saveMonitor, deleteMonitor,
  getWarehouseByGrainStoreId, uploadFile, saveGrainStore
} from '../api'

const props = defineProps({
  grainStoreId: { type: Number, default: null },
  storeName: { type: String, default: '' },
  embedded: { type: Boolean, default: false }
})
const route = useRoute()
const router = useRouter()
const grainStoreId = computed(() => props.grainStoreId ?? Number(route.query.grainStoreId))
const storeName = computed(() => props.storeName || route.query.name || '')

const planImage = ref('')
const monitors = ref([])
const warehouses = ref([])
const canvasRef = ref(null)
const scrollRef = ref(null)
const pending = ref(null)
const dialogVisible = ref(false)
const zoom = ref(1)
const panX = ref(0)
const panY = ref(0)
const panning = ref(false)
const draggingId = ref(null)
const justSaved = ref(false)
const editingMonitor = ref(null)
const editingOriginal = ref({ angle: 0 })

const canvasStyle = computed(() => ({
  transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoom.value})`,
  transformOrigin: '0 0'
}))

const formData = reactive({
  id: null,
  monitorName: '',
  cameraType: 'dome',
  warehouseId: null,
  ipAddress: '',
  port: '80',
  streamUrl: '',
  username: '',
  password: '',
  status: 1,
  posX: 0,
  posY: 0,
  angle: 0
})

// 拖动角度滑块时，同步更新平面图上对应摄像机标记的角度，实现实时预览
watch(() => formData.angle, (v) => {
  if (editingMonitor.value && formData.cameraType === 'bullet') {
    editingMonitor.value.angle = v
  }
})

onMounted(() => {
  if (!props.embedded && (isNaN(grainStoreId.value) || grainStoreId.value <= 0)) {
    ElMessage.warning('请从「粮库管理」进入监控平面图，或携带合法的 grainStoreId 参数')
    return
  }
  loadStore()
  loadMonitors()
  loadWarehouses()
})

const loadStore = async () => {
  try {
    const res = await getGrainStoreById(grainStoreId.value)
    planImage.value = res.data.planImage || ''
  } catch (e) { console.error(e) }
}
const loadMonitors = async () => {
  try {
    const res = await getMonitorByGrainStoreId(grainStoreId.value)
    monitors.value = res.data || []
  } catch (e) { ElMessage.error('加载摄像机失败') }
}
const loadWarehouses = async () => {
  try {
    const res = await getWarehouseByGrainStoreId(grainStoreId.value)
    warehouses.value = res.data || []
  } catch (e) { console.error(e) }
}

const beforeUpload = (file) => {
  const ok = file.type.startsWith('image/') && file.size / 1024 / 1024 < 10
  if (!ok) ElMessage.error('请上传小于 10MB 的图片')
  return ok
}
const handlePlanUpload = async ({ file }) => {
  try {
    const res = await uploadFile(file, 'plan')
    const store = (await getGrainStoreById(grainStoreId.value)).data
    store.planImage = res.data
    await saveGrainStore(store)
    planImage.value = res.data
    ElMessage.success('平面图已保存')
  } catch (e) { ElMessage.error('平面图上传失败') }
}

/* ---------- 缩放 ---------- */
const zoomIn = () => { zoom.value = Math.min(3, +(zoom.value + 0.2).toFixed(2)) }
const zoomOut = () => { zoom.value = Math.max(0.3, +(zoom.value - 0.2).toFixed(2)) }
const zoomReset = () => { zoom.value = 1 }
const onWheel = (e) => {
  if (e.deltaY < 0) zoomIn()
  else zoomOut()
}

/* ---------- 平移平面图（拖拽空白处） ---------- */
const startPan = (e) => {
  // 仅背景触发，标记通过 .stop 已拦截
  e.preventDefault()
  panning.value = true
  const startX = e.clientX, startY = e.clientY
  const baseX = panX.value, baseY = panY.value
  const move = (ev) => {
    panX.value = baseX + (ev.clientX - startX)
    panY.value = baseY + (ev.clientY - startY)
  }
  const up = () => {
    panning.value = false
    document.removeEventListener('mousemove', move)
    document.removeEventListener('mouseup', up)
  }
  document.addEventListener('mousemove', move)
  document.addEventListener('mouseup', up)
}

/* ---------- 双击空白新增摄像机 ---------- */
const onPlanDblClick = (e) => {
  if (!canvasRef.value) return
  const rect = canvasRef.value.getBoundingClientRect()
  const x = ((e.clientX - rect.left) / rect.width) * 100
  const y = ((e.clientY - rect.top) / rect.height) * 100
  const nx = Math.max(0, Math.min(100, +x.toFixed(2)))
  const ny = Math.max(0, Math.min(100, +y.toFixed(2)))
  const defaultAngle = getDefaultAngle(nx, ny)
  pending.value = { x: nx, y: ny }
  Object.assign(formData, {
    id: null, monitorName: '', cameraType: 'dome', warehouseId: null, ipAddress: '', port: '80',
    streamUrl: '', username: '', password: '', status: 1,
    posX: nx, posY: ny, angle: defaultAngle
  })
  dialogVisible.value = true
}

// 根据摄像机在平面图上的位置，给枪机一个朝向内部的默认角度，使标注更自然
function getDefaultAngle(x, y) {
  // 0=右, 90=下, 180=左, 270=上
  const toTop = y
  const toBottom = 100 - y
  const toLeft = x
  const toRight = 100 - x
  const min = Math.min(toTop, toBottom, toLeft, toRight)
  if (min === toTop) return 90      // 靠近上边缘 → 朝下
  if (min === toBottom) return 270  // 靠近下边缘 → 朝上
  if (min === toLeft) return 0      // 靠近左边缘 → 朝右
  return 180                        // 靠近右边缘 → 朝左
}

/* ---------- 拖拽移动摄像机（移动时不弹框） ---------- */
const startDrag = (e, m) => {
  e.preventDefault()
  const startX = e.clientX, startY = e.clientY
  let moved = false
  draggingId.value = m.id
  const move = (ev) => {
    const rect = canvasRef.value.getBoundingClientRect()
    const x = ((ev.clientX - rect.left) / rect.width) * 100
    const y = ((ev.clientY - rect.top) / rect.height) * 100
    m.posX = +Math.max(0, Math.min(100, x)).toFixed(2)
    m.posY = +Math.max(0, Math.min(100, y)).toFixed(2)
    if (Math.abs(ev.clientX - startX) > 4 || Math.abs(ev.clientY - startY) > 4) moved = true
  }
  const up = async () => {
    document.removeEventListener('mousemove', move)
    document.removeEventListener('mouseup', up)
    draggingId.value = null
    if (moved) {
      try {
        await saveMonitor({ ...m, grainStoreId: grainStoreId.value })
        ElMessage.success('位置已更新')
      } catch (err) {
        ElMessage.error('更新位置失败')
        loadMonitors()
      }
    } else {
      // 仅点击未拖动 → 打开编辑框
      editMarker(m)
    }
  }
  document.addEventListener('mousemove', move)
  document.addEventListener('mouseup', up)
}

const editMarker = (m) => {
  pending.value = null
  editingMonitor.value = m
  editingOriginal.value = { angle: m.angle || 0 }
  Object.assign(formData, JSON.parse(JSON.stringify(m)))
  if (formData.posX == null) formData.posX = 0
  if (formData.posY == null) formData.posY = 0
  if (formData.angle == null) formData.angle = 0
  if (!formData.cameraType) formData.cameraType = 'dome'
  dialogVisible.value = true
}

/* 关闭弹框：未保存的新摄像机 → 平面图上的临时标记一并隐藏；
   取消编辑时恢复地图上摄像机的原始角度 */
const onDialogClose = () => {
  if (!justSaved.value) {
    if (!formData.id) pending.value = null
    if (editingMonitor.value) {
      editingMonitor.value.angle = editingOriginal.value.angle
    }
  }
  justSaved.value = false
  editingMonitor.value = null
  editingOriginal.value = { angle: 0 }
}

const handleSave = async () => {
  if (!formData.monitorName) {
    ElMessage.error('请填写摄像机名称')
    return
  }
  const payload = { ...formData, grainStoreId: grainStoreId.value }
  try {
    await saveMonitor(payload)
    ElMessage.success('保存成功')
    justSaved.value = true
    dialogVisible.value = false
    pending.value = null
    loadMonitors()
  } catch (e) { ElMessage.error('保存失败') }
}

const handleDelete = async (id) => {
  try {
    await deleteMonitor(id)
    ElMessage.success('删除成功')
    loadMonitors()
  } catch (e) { ElMessage.error('删除失败') }
}

const onImgLoad = () => {}
const back = () => router.push('/grain-store')
</script>

<style scoped>
.toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
.store-tag { font-size: 14px; color: #606266; }
.hint { font-size: 12px; color: #909399; }
.hint i { color: #409eff; font-style: normal; font-weight: 600; }
.zoom-box { display: inline-flex; align-items: center; }
.plan-wrap { display: flex; gap: 16px; align-items: flex-start; }
.plan-scroll {
  flex: 1; min-width: 0;
  height: 68vh;
  overflow: hidden;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #f5f7fa;
  padding: 8px;
}
.plan-canvas {
  position: relative;
  width: 100%;
  line-height: 0;
  cursor: grab;
  user-select: none;
}
.plan-canvas:active { cursor: grabbing; }
.plan-img { width: 100%; display: block; user-select: none; -webkit-user-drag: none; }

/* 摄像机标记 */
.marker {
  position: absolute;
  transform: translate(-50%, -50%);
  cursor: grab;
  z-index: 2;
  filter: drop-shadow(0 1px 3px rgba(0,0,0,.45));
  transition: filter .15s;
}
.marker:active, .marker.dragging { cursor: grabbing; }
.marker.pending { filter: drop-shadow(0 0 4px rgba(103,194,58,.9)); }

/* 右侧列表 */
.marker-list { flex: 1; min-width: 240px; border: 1px solid #ebeef5; border-radius: 6px; padding: 12px; align-self: stretch; display: flex; flex-direction: column; overflow: auto; max-height:650px }
.marker-list-title { font-weight: 600; margin-bottom: 10px; flex: none; }
.marker-list-body { flex: 1; overflow-y: auto; }
.marker-item { display: flex; align-items: center; gap: 8px; padding: 6px 0; border-bottom: 1px dashed #f0f0f0; cursor: pointer; }
.marker-item:hover { background: #f5f7fa; }
.marker-item .name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.marker-item .type-tag { font-size: 12px; color: #409eff; background: #ecf5ff; border-radius: 4px; padding: 1px 6px; flex: none; }

.angle-row { display: flex; align-items: center; width: 100%; }
.angle-preview {
  margin-left: 16px;
  width: 78px; height: 78px;
  display: flex; align-items: center; justify-content: center;
  background: #f5f7fa; border-radius: 6px; border: 1px solid #ebeef5;
  flex: none;
}

/* 编辑弹窗默认可拖动，并放在右侧，不遮挡平面图中心 */
.cam-dialog :deep(.el-dialog) {
  margin-left: auto;
  margin-right: 24px;
  margin-top: 10vh;
}
</style>
